import type { BackupEntityCollector } from "../../application/services/backup-service.js";
import type { RequestContext } from "../../application/context/request-context.js";

export interface PlatformRepositoriesForBackup {
  equipmentRepo?: any;
  instrumentRepo?: any;
  safetyDeviceRepo?: any;
  inspectionRepo?: any;
  checklistRepo?: any;
  reportRepo?: any;
  recommendationRepo?: any;
  ndtAssetRepo?: any;
  ndtProcedureRepo?: any;
  ndtCatalogRepo?: any;
  minutaRepo?: any;
  projectRepo?: any;
  adminRepo?: any;
  identityRepo?: any;
  companyRepo?: any;
}

export function createDefaultBackupCollectors(
  repos: PlatformRepositoriesForBackup
): BackupEntityCollector[] {
  const collectors: BackupEntityCollector[] = [];

  // 1. Equipamentos
  if (repos.equipmentRepo) {
    collectors.push({
      entityType: "equipments",
      exportEntities: async (_ctx: RequestContext, clientId?: string) => {
        if (repos.equipmentRepo.listAll) {
          const list = await repos.equipmentRepo.listAll();
          return clientId ? list.filter((e: any) => e.clientId === clientId) : list;
        }
        if (repos.equipmentRepo.listEquipments && clientId) {
          return repos.equipmentRepo.listEquipments(clientId);
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.equipmentRepo.saveEquipment) {
            await repos.equipmentRepo.saveEquipment(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 2. Instrumentos
  if (repos.instrumentRepo) {
    collectors.push({
      entityType: "instruments",
      exportEntities: async (_ctx: RequestContext, clientId?: string) => {
        if (repos.instrumentRepo.listAll) {
          const list = await repos.instrumentRepo.listAll();
          return clientId ? list.filter((i: any) => i.clientId === clientId) : list;
        }
        if (repos.instrumentRepo.listInstruments && clientId) {
          return repos.instrumentRepo.listInstruments(clientId);
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.instrumentRepo.saveInstrument) {
            await repos.instrumentRepo.saveInstrument(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 3. Dispositivos de Segurança
  if (repos.safetyDeviceRepo) {
    collectors.push({
      entityType: "safety_devices",
      exportEntities: async (_ctx: RequestContext, clientId?: string) => {
        if (repos.safetyDeviceRepo.listAll) {
          const list = await repos.safetyDeviceRepo.listAll();
          return clientId ? list.filter((d: any) => d.clientId === clientId) : list;
        }
        if (repos.safetyDeviceRepo.listSafetyDevices && clientId) {
          return repos.safetyDeviceRepo.listSafetyDevices(clientId);
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.safetyDeviceRepo.saveSafetyDevice) {
            await repos.safetyDeviceRepo.saveSafetyDevice(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 4. Projetos & Unidades
  if (repos.projectRepo) {
    collectors.push({
      entityType: "projects",
      exportEntities: async (_ctx: RequestContext, clientId?: string) => {
        if (repos.projectRepo.listProjects) {
          const list = await repos.projectRepo.listProjects();
          return clientId ? list.filter((p: any) => p.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.projectRepo.saveProject) {
            await repos.projectRepo.saveProject(item);
            count++;
          }
        }
        return count;
      }
    });

    collectors.push({
      entityType: "unit_areas",
      exportEntities: async (_ctx: RequestContext, clientId?: string) => {
        if (repos.projectRepo.listUnitAreas) {
          const list = await repos.projectRepo.listUnitAreas();
          return clientId ? list.filter((a: any) => a.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.projectRepo.saveUnitArea) {
            await repos.projectRepo.saveUnitArea(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 5. Inspeções
  if (repos.inspectionRepo) {
    collectors.push({
      entityType: "inspections",
      exportEntities: async (ctx: RequestContext, clientId?: string) => {
        if (repos.inspectionRepo.listAll) {
          const list = await repos.inspectionRepo.listAll(ctx);
          return clientId ? list.filter((i: any) => i.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.inspectionRepo.save) {
            await repos.inspectionRepo.save(ctx, item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 6. Checklists
  if (repos.checklistRepo) {
    collectors.push({
      entityType: "checklists",
      exportEntities: async (ctx: RequestContext) => {
        if (repos.checklistRepo.listAll) {
          return repos.checklistRepo.listAll(ctx);
        }
        return [];
      },
      restoreEntities: async (ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.checklistRepo.save) {
            await repos.checklistRepo.save(ctx, item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 7. Relatórios de Integridade
  if (repos.reportRepo) {
    collectors.push({
      entityType: "integrity_reports",
      exportEntities: async (ctx: RequestContext, clientId?: string) => {
        if (repos.reportRepo.listAll) {
          const list = await repos.reportRepo.listAll(ctx);
          return clientId ? list.filter((r: any) => r.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.reportRepo.save) {
            await repos.reportRepo.save(ctx, item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 8. Recomendações
  if (repos.recommendationRepo) {
    collectors.push({
      entityType: "recommendations",
      exportEntities: async (ctx: RequestContext, clientId?: string) => {
        if (repos.recommendationRepo.listAll) {
          const list = await repos.recommendationRepo.listAll(ctx);
          return clientId ? list.filter((r: any) => r.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.recommendationRepo.save) {
            await repos.recommendationRepo.save(ctx, item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 9. Ativos de END (Equipamentos/Aparelhos, Transdutores, Blocos, Yokes)
  if (repos.ndtAssetRepo) {
    collectors.push({
      entityType: "ndt_assets",
      exportEntities: async (_ctx: RequestContext) => {
        if (repos.ndtAssetRepo.listAll) {
          return repos.ndtAssetRepo.listAll();
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.ndtAssetRepo.save) {
            await repos.ndtAssetRepo.save(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 10. Procedimentos de END
  if (repos.ndtProcedureRepo) {
    collectors.push({
      entityType: "ndt_procedures",
      exportEntities: async (_ctx: RequestContext) => {
        if (repos.ndtProcedureRepo.listAll) {
          return repos.ndtProcedureRepo.listAll();
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.ndtProcedureRepo.save) {
            await repos.ndtProcedureRepo.save(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 11. Catálogos Técnicos de END
  if (repos.ndtCatalogRepo) {
    collectors.push({
      entityType: "ndt_catalogs",
      exportEntities: async (_ctx: RequestContext) => {
        if (repos.ndtCatalogRepo.listAll) {
          return repos.ndtCatalogRepo.listAll();
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.ndtCatalogRepo.save) {
            await repos.ndtCatalogRepo.save(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 12. Minutas Técnicas
  if (repos.minutaRepo) {
    collectors.push({
      entityType: "minutas",
      exportEntities: async (ctx: RequestContext, clientId?: string) => {
        if (repos.minutaRepo.listAll) {
          const list = await repos.minutaRepo.listAll(ctx);
          return clientId ? list.filter((m: any) => m.clientId === clientId) : list;
        }
        return [];
      },
      restoreEntities: async (ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.minutaRepo.save) {
            await repos.minutaRepo.save(ctx, item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 13. Empresas & Clientes
  if (repos.companyRepo || repos.adminRepo) {
    const compRepo = repos.companyRepo || repos.adminRepo;
    collectors.push({
      entityType: "companies",
      exportEntities: async (_ctx: RequestContext) => {
        if (compRepo.listCompanies) {
          return compRepo.listCompanies();
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (compRepo.saveCompany) {
            await compRepo.saveCompany(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  // 14. Profissionais
  if (repos.identityRepo) {
    collectors.push({
      entityType: "professionals",
      exportEntities: async (_ctx: RequestContext) => {
        if (repos.identityRepo.listProfessionals) {
          return repos.identityRepo.listProfessionals();
        }
        return [];
      },
      restoreEntities: async (_ctx: RequestContext, entities: readonly unknown[]) => {
        let count = 0;
        for (const item of entities as any[]) {
          if (repos.identityRepo.saveProfessional) {
            await repos.identityRepo.saveProfessional(item);
            count++;
          }
        }
        return count;
      }
    });
  }

  return collectors;
}
